import { useState, useEffect } from 'react';
import { Plus, Save, AlertTriangle, ChevronDown, Image, PenLine } from 'lucide-react';
import {
  Package,
  Percent,
  IndianRupee,
  LayoutGrid,
  CalendarDays,
  CalendarCheck,
} from 'lucide-react';

import Sidebar from '../../component/b2c/sidebar/sidebar';
import NavBar from '../../component/b2c/navBar/navBar';
import { useMyContext } from '../../context/MyContext';
import { Base_Url, setOffer } from '../../config/config';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';

export default function OfferSessionPanel() {
  const { isToggleSidebar, isDarkMode } = useMyContext();

  const navigate = useNavigate();

  const [newOffer, setNewOffer] = useState({
    threshold: '',
    description: '',
    discountType: "Percentage",
    category: 'All',
    startDate: '',
    endDate: '',
    image: ''
  });

  const [editingId, setEditingId] = useState(null);
  const [savedMessage, setSavedMessage] = useState('');
  const [messageType, setMessageType] = useState('success');

  const [activeTab, setActiveTab] = useState('current');

  const categories = ['All', 'shipment cashback'];

  // Set today's date as default start date when component mounts
  useEffect(() => {
    const today = new Date().toISOString().split('T')[0];
    const nextMonth = new Date();
    nextMonth.setMonth(nextMonth.getMonth() + 1);

    setNewOffer(prev => ({
      ...prev,
      startDate: today,
      endDate: nextMonth.toISOString().split('T')[0]
    }));
  }, []);

  const validateForm = () => {
    const { threshold, discountType, startDate, endDate, description, image } = newOffer;

    if (!threshold || !discountType || !startDate || !endDate || !image || !description) {
      showMessage("Please fill all required fields", "error");
      return false;
    }

    if (new Date(startDate) > new Date(endDate)) {
      showMessage("End date must be after start date", "error");
      return false;
    }

    return true;
  };

  const token = localStorage.getItem('token');
  const cus_id = localStorage.getItem('cus_id');

  const addOffer = async () => {
    if (!validateForm()) return;

    const thresholdValue = parseFloat(newOffer.threshold);
    try {
      const formData = new FormData();
      formData.append('category', newOffer.category);
      formData.append('discount_type', newOffer.discountType);
      formData.append('discount_value', thresholdValue);
      formData.append('description', newOffer.description);

      const formatDate = (dateStr) => {
        const date = new Date(dateStr);
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        return `${day}/${month}/${year}`;
      };

      formData.append('start_date', formatDate(newOffer.startDate));
      formData.append('end_date', formatDate(newOffer.endDate));

      if (newOffer.image) {
        formData.append('image', newOffer.image);
      }

      const response = await fetch(Base_Url + setOffer, {
        method: 'POST',
        headers: {
          "cus_id": cus_id,
          "token": token,
        },
        body: formData
      });

      const result = await response.json();
      if (result.status === "SUCCESS") {
        toast.success(result.message);
        navigate('/dashboard')
      } else {
        toast.error(result.message);
      }
    } catch (error) {
      toast.error("Failed to add offer. Please try again.", "error");
    }
  };

  const showMessage = (message, type = 'success') => {
    setSavedMessage(message);
    setMessageType(type);

    setTimeout(() => {
      setSavedMessage('');
    }, 3000);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewOffer(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const getStatusClass = (status) => {
    switch (status) {
      case 'active':
        return 'bg-black text-white';
      case 'scheduled':
        return 'bg-gray-200 text-black';
      case 'expired':
        return 'bg-gray-100 text-gray-500';
      default:
        return 'bg-gray-100 text-gray-500';
    }
  };

  return (
    <div className={`h-screen flex flex-col overflow-hidden ${isDarkMode ? 'dark:bg-black' : 'bg-white'}`}>
      <div className="z-10">
        <NavBar />
      </div>

      <div className="flex flex-1 overflow-hidden">
        {isToggleSidebar && (
          <div className={`fixed z-20 h-full w-64 md:relative duration-300 bg-white dark:bg-black dark:text-white md:bg-transparent mt-4 md:w-64 md:z-20 border-r border-gray-200 dark:border-gray-800`}
            style={
              window.innerWidth < 768
                ? { top: "113px", height: "calc(100vh - 115px)", overflowY: 'auto' }
                : {}
            }>
            <Sidebar />
          </div>
        )}

        <div className="flex-1 bg-gray-100 dark:bg-black overflow-y-auto p-4">
          <div className="max-w-5xl mx-auto">
            {/* Header */}
            <div className="mb-4 pb-4 border-b border-black dark:border-white">
              <div className="flex items-center">
                <div className="p-2 rounded border border-black dark:border-white text-black dark:text-white mr-3">
                  <Package size={18} />
                </div>
                <h2 className="text-lg font-semibold tracking-tight text-black dark:text-white">
                  Logistics Offer Management
                </h2>
              </div>
            </div>

            {/* Tabs */}
            <div className="flex mb-4 border-b border-gray-200 dark:border-gray-800">
              {['current'].map((tab) => (
                <button
                  key={tab}
                  className={`px-4 py-2 text-xs font-medium tracking-wide uppercase transition-colors relative ${activeTab === tab
                    ? 'text-black dark:text-white'
                    : 'text-gray-400 dark:text-gray-600 hover:text-black dark:hover:text-white'
                    }`}
                  onClick={() => setActiveTab(tab)}
                >
                  {tab.charAt(0).toUpperCase() + tab.slice(1)} Offers
                  {activeTab === tab && (
                    <div className="absolute -bottom-px left-0 w-full h-0.5 bg-black dark:bg-white"></div>
                  )}
                </button>
              ))}
            </div>

            {activeTab === 'current' && (
              <>
                {/* Add/Edit Offer Form */}
                <div className="bg-white dark:bg-black text-black dark:text-white rounded-md p-3 mb-8 border border-gray-200 dark:border-gray-800">
                  <div className="flex items-center mb-6">
                    <div className="p-1.5 border border-gray-300 dark:border-gray-700 text-black dark:text-white rounded mr-2">
                      {editingId ? <PenLine size={14} /> : <Plus size={14} />}
                    </div>
                    <h3 className="text-sm font-semibold text-black dark:text-white tracking-tight">
                      {editingId ? 'Edit Offer' : 'Create New Offer'}
                    </h3>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">

                    <div className="group">
                      <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5 group-focus-within:text-black dark:group-focus-within:text-white transition-colors">
                        Discount *
                      </label>

                      <div className="flex gap-2">
                        {/* Dropdown for Discount Type */}
                        <div className="relative w-1/2">
                          <select
                            name="discountType"
                            value={newOffer.discountType}
                            onChange={handleInputChange}
                            className="w-full text-sm border rounded border-gray-300 dark:border-gray-700 pl-3 pr-8 py-2 appearance-none focus:outline-none focus:ring-1 focus:ring-black dark:focus:ring-white bg-white dark:bg-black text-black dark:text-white transition-all"
                          >
                            <option value="Percentage">Percentage</option>
                            <option value="Flat">Flat</option>
                          </select>
                          <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                            <ChevronDown size={14} className="text-gray-400" />
                          </div>
                        </div>

                        {/* Input for Discount Value */}
                        <div className="relative w-1/2">
                          <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                            {newOffer.discountType === "Percentage" ? (
                              <Percent size={14} className="text-gray-400" />
                            ) : (
                              <IndianRupee size={14} className="text-gray-400" />
                            )}
                          </div>
                          <input
                            type="number"
                            name="threshold"
                            value={newOffer.threshold}
                            onChange={handleInputChange}
                            placeholder={newOffer.discountType === "Percentage" ? "E.g. 10" : "E.g. 500"}
                            min="0"
                            max={newOffer.discountType === "Percentage" ? "100" : undefined}
                            className="w-full text-sm border rounded border-gray-300 dark:border-gray-700 pl-8 pr-3 py-2 focus:outline-none focus:ring-1 focus:ring-black dark:focus:ring-white bg-white dark:bg-black text-black dark:text-white transition-all"
                          />
                        </div>
                      </div>
                    </div>

                    <div className="group">
                      <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5 group-focus-within:text-black dark:group-focus-within:text-white transition-colors">
                        Category *
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                          <LayoutGrid size={14} className="text-gray-400" />
                        </div>
                        <select
                          name="category"
                          value={newOffer.category}
                          onChange={handleInputChange}
                          className="w-full text-sm border rounded border-gray-300 dark:border-gray-700 pl-8 pr-8 py-2 appearance-none focus:outline-none focus:ring-1 focus:ring-black dark:focus:ring-white bg-white dark:bg-black text-black dark:text-white transition-all"
                        >
                          {categories.filter(category => category !== 'All').map(category => (
                            <option key={category} value={category}>{category}</option>
                          ))}
                        </select>
                        <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                          <ChevronDown size={14} className="text-gray-400" />
                        </div>
                      </div>
                    </div>

                    <div className="group">
                      <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5 group-focus-within:text-black dark:group-focus-within:text-white transition-colors">
                        Start Date *
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                          <CalendarDays size={14} className="text-gray-400" />
                        </div>
                        <input
                          type="date"
                          name="startDate"
                          value={newOffer.startDate}
                          onChange={handleInputChange}
                          className="w-full text-sm border rounded border-gray-300 dark:border-gray-700 pl-8 pr-3 py-2 focus:outline-none focus:ring-1 focus:ring-black dark:focus:ring-white bg-white dark:bg-black text-black dark:text-white transition-all"
                        />
                      </div>
                    </div>

                    <div className="group">
                      <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5 group-focus-within:text-black dark:group-focus-within:text-white transition-colors">
                        End Date *
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                          <CalendarCheck size={14} className="text-gray-400" />
                        </div>
                        <input
                          type="date"
                          name="endDate"
                          value={newOffer.endDate}
                          onChange={handleInputChange}
                          className="w-full text-sm border rounded border-gray-300 dark:border-gray-700 pl-8 pr-3 py-2 focus:outline-none focus:ring-1 focus:ring-black dark:focus:ring-white bg-white dark:bg-black text-black dark:text-white transition-all"
                        />
                      </div>
                    </div>

                    <div className="group">
                      <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5 group-focus-within:text-black dark:group-focus-within:text-white transition-colors">
                        Description
                      </label>
                      <div className="relative">
                        <input
                          type="text"
                          name="description"
                          value={newOffer.description}
                          onChange={handleInputChange}
                          placeholder="Short description"
                          className="w-full text-sm border rounded border-gray-300 dark:border-gray-700 pl-3 pr-3 py-2 focus:outline-none focus:ring-1 focus:ring-black dark:focus:ring-white bg-white dark:bg-black text-black dark:text-white transition-all"
                        />
                      </div>
                    </div>

                    <div className="group">
                      <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5 group-focus-within:text-black dark:group-focus-within:text-white transition-colors">
                        Offer Image
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                          <Image size={14} className="text-gray-400" />
                        </div>
                        <input
                          type="file"
                          name="image"
                          accept="image/*"
                          onChange={(e) =>
                            setNewOffer({ ...newOffer, image: e.target.files[0] })
                          }
                          className="w-full text-sm border rounded border-gray-300 dark:border-gray-700 pl-8 pr-3 py-1.5 focus:outline-none focus:ring-1 focus:ring-black dark:focus:ring-white bg-white dark:bg-black text-black dark:text-white transition-all file:mr-3 file:py-1 file:px-3 file:rounded file:border file:border-black dark:file:border-white file:text-xs file:font-medium file:bg-black file:text-white dark:file:bg-white dark:file:text-black hover:file:opacity-80"
                        />
                      </div>

                      {newOffer.image && (
                        <div className="mt-2">
                          <img
                            src={URL.createObjectURL(newOffer.image)}
                            alt="Preview"
                            className="w-20 h-20 object-cover rounded border border-gray-300 dark:border-gray-700"
                          />
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="mt-8 flex justify-end">
                    <button
                      onClick={addOffer}
                      className="px-5 py-2 bg-black text-white dark:bg-white dark:text-black text-sm font-medium rounded hover:opacity-80 transition-all flex items-center"
                    >
                      <Plus size={14} className="mr-1.5" /> Add Offer
                    </button>
                  </div>
                </div>
              </>
            )}

            {/* Notification */}
            {savedMessage && (
              <div className={`fixed bottom-4 right-4 px-4 py-3 text-sm rounded border flex items-center ${messageType === 'success'
                ? 'bg-black text-white border-black dark:bg-white dark:text-black dark:border-white'
                : 'bg-white text-black border-black dark:bg-black dark:text-white dark:border-white'
                }`}>
                {messageType === 'success' ? (
                  <Save size={14} className="mr-2" />
                ) : (
                  <AlertTriangle size={14} className="mr-2" />
                )}
                {savedMessage}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}